/**
 * AccountPageViewModel - Account Page Orchestration
 * 
 * Manages complete account page functionality for PageContentAccount component
 * Handles user account data, navigation tiles, and user interactions
 * 
 * Cross-platform implementation with dependency injection
 */

import { useCallback, useState, useEffect, useMemo, useRef } from 'react'
import type {
  IPlatformServices,
  IAccountPageService,
  UserAccountSummary
} from '../contracts'
// Type-only imports (erased at compile time — no @restaurant/core React Native
// runtime dependency). Using the canonical core types keeps these structurally
// aligned with IAccountPageService, which returns the core AccountPageTile.
import type { AccountPageTile, PageContentAccountProps } from '@restaurant/core'

// ViewModel dependencies interface
export interface AccountPageViewModelDependencies {
  accountPageService?: IAccountPageService
}

// ViewModel state interface
export interface AccountPageViewModelState {
  // User account data
  userSummary: UserAccountSummary | null
  
  // Navigation tiles
  tiles: AccountPageTile[]
  availableTiles: AccountPageTile[]
  
  // Loading states
  loading: boolean
  error: string | null
  
  // Computed user properties
  isUserAuthenticated: boolean
  hasUserData: boolean
  userDisplayName: string | null
  userEmail: string | null
  userPhone: string | undefined
  userRewardPoints: number
  userTier: string | null
  isUserVerified: boolean
}

// ViewModel actions interface
export interface AccountPageViewModelActions {
  // Data management
  refreshUserData: () => Promise<void>
  refreshTiles: () => Promise<void>
  refreshAll: () => Promise<void>
  
  // Tile interactions
  handleTileClick: (tile: AccountPageTile, index: number) => Promise<void>
  
  // State management
  clearError: () => void
  setLoading: (loading: boolean) => void
  setError: (error: string) => void
  
  // Component integration
  getPageContentAccountProps: () => Partial<PageContentAccountProps>
}

// Combined ViewModel interface
export interface AccountPageViewModel extends AccountPageViewModelState, AccountPageViewModelActions {}

/**
 * Platform-agnostic AccountPageViewModel factory
 * Uses dependency injection for platform-specific services
 */
export const createAccountPageViewModel = (
  _services: IPlatformServices,
  dependencies?: AccountPageViewModelDependencies
) => {
  return (): AccountPageViewModel => {
    // State management
    const [userSummary, setUserSummary] = useState<UserAccountSummary | null>(null)
    const [tiles, setTiles] = useState<AccountPageTile[]>([])
    const [loading, setLoadingState] = useState(true)
    const [error, setErrorState] = useState<string | null>(null)
    
    // Use ref to track loading state for callbacks to avoid stale closures
    const loadingRef = useRef(loading)
    loadingRef.current = loading

    // Get service from dependencies or throw error
    const accountPageService = dependencies?.accountPageService
    if (!accountPageService) {
      throw new Error('AccountPageViewModel requires accountPageService dependency')
    }

    // Computed properties
    const isUserAuthenticated = useMemo(() => userSummary !== null, [userSummary])
    const hasUserData = useMemo(() => userSummary !== null, [userSummary])
    const userDisplayName = useMemo(() => userSummary?.displayName || null, [userSummary])
    const userEmail = useMemo(() => userSummary?.email || null, [userSummary])
    const userPhone = useMemo(() => userSummary?.phone, [userSummary])
    const userRewardPoints = useMemo(() => userSummary?.rewardPoints || 0, [userSummary])
    const userTier = useMemo(() => userSummary?.tier || null, [userSummary])
    const isUserVerified = useMemo(() => userSummary?.isVerified || false, [userSummary])

    // Filter tiles based on user access
    const availableTiles = useMemo(() => {
      return tiles.filter(_tile => {
        // For now, return all tiles - access filtering will be handled in refreshTiles
        return true
      })
    }, [tiles])

    // Data loading functions
    const refreshUserData = useCallback(async () => {
      try {
        setLoadingState(true)
        setErrorState(null)
        
        const userData = await accountPageService.getUserAccountSummary()
        setUserSummary(userData)
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load user data'
        setErrorState(errorMessage)
        setUserSummary(null)
      } finally {
        setLoadingState(false)
      }
    }, [accountPageService])

    const refreshTiles = useCallback(async () => {
      try {
        setLoadingState(true)
        setErrorState(null)
        
        const navigationTiles = await accountPageService.getAccountNavigationTiles()
        
        // Filter tiles based on user access
        const accessibleTiles: AccountPageTile[] = []
        for (const tile of navigationTiles) {
          try {
            const hasAccess = await accountPageService.hasAccessToTile(tile.id)
            if (hasAccess) {
              accessibleTiles.push(tile)
            }
          } catch (accessError) {
            // If access check fails, include the tile by default
            accessibleTiles.push(tile)
          }
        }
        
        setTiles(accessibleTiles)
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load tiles'
        setErrorState(errorMessage)
        setTiles([])
      } finally {
        setLoadingState(false)
      }
    }, [accountPageService])

    const refreshAll = useCallback(async () => {
      setLoadingState(true)
      setErrorState(null)
      
      try {
        // Run both operations in parallel but handle errors independently
        const [userResult, tilesResult] = await Promise.allSettled([
          accountPageService.getUserAccountSummary(),
          accountPageService.getAccountNavigationTiles()
        ])
        
        // Handle user data result
        if (userResult.status === 'fulfilled') {
          setUserSummary(userResult.value)
        } else {
          setErrorState(userResult.reason?.message || 'Failed to load user data')
          setUserSummary(null)
        }
        
        // Handle tiles result
        if (tilesResult.status === 'fulfilled') {
          // Filter tiles based on user access
          const navigationTiles = tilesResult.value
          const accessibleTiles: AccountPageTile[] = []
          
          for (const tile of navigationTiles) {
            try {
              const hasAccess = await accountPageService.hasAccessToTile(tile.id)
              if (hasAccess) {
                accessibleTiles.push(tile)
              }
            } catch {
              // If access check fails, include the tile by default
              accessibleTiles.push(tile)
            }
          }
          
          setTiles(accessibleTiles)
        } else {
          setErrorState(tilesResult.reason?.message || 'Failed to load tiles')
          setTiles([])
        }
      } finally {
        setLoadingState(false)
      }
    }, [accountPageService])

    // Tile interaction handlers
    const handleTileClick = useCallback(async (tile: AccountPageTile, _index: number) => {
      // Don't navigate if loading (use ref for current state)
      if (loadingRef.current) {
        return
      }
      
      try {
        setErrorState(null)
        await accountPageService.handleTileNavigation(tile.id)
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Navigation failed'
        setErrorState(errorMessage)
      }
    }, [accountPageService])

    // State management helpers
    const clearError = useCallback(() => {
      setErrorState(null)
    }, [])

    const setLoading = useCallback((loadingState: boolean) => {
      setLoadingState(loadingState)
    }, [])

    const setError = useCallback((errorMessage: string) => {
      setErrorState(errorMessage)
    }, [])

    // Component integration
    const getPageContentAccountProps = useCallback((): Partial<PageContentAccountProps> => {
      return {
        tiles: availableTiles,
        onTileClick: handleTileClick,
        loading,
        disabled: loading,
        testID: 'account-page-content',
        accessibilityLabel: 'Account navigation page',
      }
    }, [availableTiles, handleTileClick, loading])

    // Initialize data on mount
    useEffect(() => {
      refreshAll()
    }, [refreshAll])

    // Return ViewModel interface
    return {
      // State
      userSummary,
      tiles,
      availableTiles,
      loading,
      error,
      isUserAuthenticated,
      hasUserData,
      userDisplayName,
      userEmail,
      userPhone,
      userRewardPoints,
      userTier,
      isUserVerified,
      
      // Actions
      refreshUserData,
      refreshTiles,
      refreshAll,
      handleTileClick,
      clearError,
      setLoading,
      setError,
      getPageContentAccountProps,
    }
  }
}